import { DEFAULT_DATA, FREQUENCIES } from '@/shared/constants/config';
import { todayStr } from '@/shared/utils/dateHelpers';
import { migrateSavings } from '@/features/savings/utils/savingsMigration';
import { migrateNetWorth } from '@/features/networth/utils/netWorthHelpers';

const BACKUP_VERSION = 1;

const FREQ_SET = new Set(FREQUENCIES);
const TXN_TYPES = new Set(['income', 'expense']);
const SETTINGS_KEYS = new Set([
  'budgetMode',
  'currency',
  'darkMode',
  'defaultPayment',
  'displayNames',
  'budgetPeriod',
]);

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const BUDGET_PERIOD_RE = /^\d{4}-\d{1,2}$/;

const MAX_STRING = 500;
const MAX_SHORT_STRING = 200;

function cleanString(v, max = MAX_STRING) {
  if (typeof v !== 'string') return null;
  const trimmed = v.slice(0, max);
  return trimmed;
}

function cleanNumber(v) {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function cleanDate(v) {
  if (typeof v !== 'string') return null;
  if (ISO_DATE_RE.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function cleanIsoDateTime(v) {
  if (typeof v !== 'string') return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function cleanBool(v) {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
}

function validateTxn(t, idx) {
  if (!t || typeof t !== 'object') {
    return { ok: false, reason: `txn[${idx}] is not an object` };
  }
  const date = cleanDate(t.date);
  if (!date) return { ok: false, reason: `txn[${idx}] has invalid date` };
  const amount = cleanNumber(t.amount);
  if (amount == null) return { ok: false, reason: `txn[${idx}] has invalid amount` };

  const clean = {
    id: cleanString(t.id, 100) || 'tx-' + Date.now().toString(36) + '-' + idx.toString(36),
    date,
    amount,
    description: cleanString(t.description, MAX_SHORT_STRING) || '',
    category: cleanString(t.category, MAX_SHORT_STRING) || '',
    who: cleanString(t.who, MAX_SHORT_STRING) || '',
    notes: cleanString(t.notes, MAX_STRING) || '',
    paymentMethod: cleanString(t.paymentMethod, 100) || '',
  };
  if (TXN_TYPES.has(t.type)) clean.type = t.type;
  const sourceBillId = cleanString(t.sourceBillId, 100);
  if (sourceBillId) clean.sourceBillId = sourceBillId;
  if (Array.isArray(t.tags)) {
    const cleanTags = t.tags
      .filter(tag => typeof tag === 'string')
      .map(tag => tag.trim().toLowerCase())
      .filter(Boolean);
    if (cleanTags.length > 0) clean.tags = cleanTags;
  }
  return { ok: true, clean };
}

function validateCustomSchedule(cs) {
  if (!cs || typeof cs !== 'object') return null;
  if (cs.type === 'every-n-weeks') {
    const intervalWeeks = cleanNumber(cs.intervalWeeks);
    const dayOfWeek = cleanNumber(cs.dayOfWeek);
    if (intervalWeeks == null || intervalWeeks < 1 || intervalWeeks > 52) return null;
    if (dayOfWeek == null || dayOfWeek < 0 || dayOfWeek > 6) return null;
    const out = { type: 'every-n-weeks', intervalWeeks, dayOfWeek };
    const anchorDate = cleanDate(cs.anchorDate);
    if (anchorDate) out.anchorDate = anchorDate;
    return out;
  }
  if (cs.type === 'nth-weekday-of-month') {
    const nth = cleanNumber(cs.nth);
    const dayOfWeek = cleanNumber(cs.dayOfWeek);
    if (nth == null || ![1, 2, 3, 4, -1].includes(nth)) return null;
    if (dayOfWeek == null || dayOfWeek < 0 || dayOfWeek > 6) return null;
    return { type: 'nth-weekday-of-month', nth, dayOfWeek };
  }
  return null;
}

function validateBill(b, idx) {
  if (!b || typeof b !== 'object') {
    return { ok: false, reason: `bill[${idx}] is not an object` };
  }
  const name = cleanString(b.name, MAX_SHORT_STRING);
  if (!name) return { ok: false, reason: `bill[${idx}] is missing a name` };
  const amount = cleanNumber(b.amount);
  if (amount == null) return { ok: false, reason: `bill[${idx}] has invalid amount` };
  const frequency = FREQ_SET.has(b.frequency) ? b.frequency : null;
  if (!frequency) return { ok: false, reason: `bill[${idx}] has invalid frequency` };

  const clean = {
    id: cleanString(b.id, 100) || 'bill-' + Date.now().toString(36) + '-' + idx.toString(36),
    name,
    amount,
    frequency,
    category: cleanString(b.category, MAX_SHORT_STRING) || '',
  };
  const dueDate = cleanDate(b.dueDate);
  if (dueDate) clean.dueDate = dueDate;
  const autoPay = cleanBool(b.autoPay);
  if (autoPay != null) clean.autoPay = autoPay;
  const autoGenerate = cleanBool(b.autoGenerate);
  if (autoGenerate != null) clean.autoGenerate = autoGenerate;
  const createdAt = cleanIsoDateTime(b.createdAt);
  if (createdAt) clean.createdAt = createdAt;
  const lastGeneratedDate = cleanDate(b.lastGeneratedDate);
  if (lastGeneratedDate) clean.lastGeneratedDate = lastGeneratedDate;
  if (frequency === 'Custom') {
    const cs = validateCustomSchedule(b.customSchedule);
    if (cs) clean.customSchedule = cs;
  }
  return { ok: true, clean };
}

function validateBudgets(raw, warnings) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [period, cats] of Object.entries(raw)) {
    if (!BUDGET_PERIOD_RE.test(period)) {
      warnings.push(`Dropped budget entry with invalid period key: ${period}`);
      continue;
    }
    if (!cats || typeof cats !== 'object') continue;
    const clean = {};
    for (const [cat, amt] of Object.entries(cats)) {
      const n = cleanNumber(amt);
      if (n == null) {
        warnings.push(`Dropped ${period}/${cat}: non-numeric budget amount`);
        continue;
      }
      clean[cleanString(cat, MAX_SHORT_STRING) || cat] = n;
    }
    out[period] = clean;
  }
  return out;
}

function validateCats(raw, warnings) {
  if (!raw || typeof raw !== 'object') return DEFAULT_DATA.cats;
  const income = Array.isArray(raw.income)
    ? raw.income.map((c) => cleanString(c, MAX_SHORT_STRING)).filter(Boolean)
    : DEFAULT_DATA.cats.income;
  const expense = {};
  const src = raw.expense && typeof raw.expense === 'object' ? raw.expense : DEFAULT_DATA.cats.expense;
  for (const [group, subs] of Object.entries(src)) {
    if (!Array.isArray(subs)) {
      warnings.push(`Categories: dropped group "${group}" (not an array)`);
      continue;
    }
    const cleanGroup = cleanString(group, MAX_SHORT_STRING);
    if (!cleanGroup) continue;
    expense[cleanGroup] = subs.map((c) => cleanString(c, MAX_SHORT_STRING)).filter(Boolean);
  }
  return { income, expense };
}

function validateSettings(raw, warnings) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_DATA.settings };
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!SETTINGS_KEYS.has(k)) {
      warnings.push(`Settings: dropped unknown key "${k}"`);
      continue;
    }
    if (k === 'darkMode') {
      const b = cleanBool(v);
      if (b != null) out[k] = b;
    } else if (k === 'displayNames') {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const names = {};
        for (const [email, name] of Object.entries(v)) {
          const cleanName = cleanString(name, MAX_SHORT_STRING);
          if (cleanName) names[String(email).slice(0, MAX_SHORT_STRING)] = cleanName;
        }
        out[k] = names;
      }
    } else {
      const s = cleanString(v, MAX_SHORT_STRING);
      if (s != null) out[k] = s;
    }
  }
  return { ...DEFAULT_DATA.settings, ...out };
}

function validateCategoryMappings(raw, warnings) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [desc, cat] of Object.entries(raw)) {
    const d = cleanString(desc, MAX_STRING);
    const c = cleanString(cat, MAX_SHORT_STRING);
    if (d && c) out[d] = c;
    else warnings.push('Dropped a malformed category mapping entry');
  }
  return out;
}

function validateShoppingLists(raw, warnings) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [listId, list] of Object.entries(raw)) {
    if (!list || typeof list !== 'object') {
      warnings.push(`Shopping list "${listId}" is not an object, skipped`);
      continue;
    }
    const name = cleanString(list.name, MAX_SHORT_STRING);
    if (!name) {
      warnings.push(`Shopping list "${listId}" has no name, skipped`);
      continue;
    }
    const id = cleanString(list.id, 100) || listId;
    const createdAt = cleanIsoDateTime(list.createdAt) || new Date().toISOString();
    const items = {};
    if (list.items && typeof list.items === 'object' && !Array.isArray(list.items)) {
      for (const [itemId, item] of Object.entries(list.items)) {
        if (!item || typeof item !== 'object') continue;
        const itemName = cleanString(item.name, MAX_SHORT_STRING);
        if (!itemName) continue;
        const cleanItem = {
          id: cleanString(item.id, 100) || itemId,
          name: itemName,
          qty: item.qty != null ? cleanNumber(item.qty) : null,
          category: cleanString(item.category, MAX_SHORT_STRING) || null,
          notes: cleanString(item.notes, MAX_STRING) || null,
          checkedAt: cleanDate(item.checkedAt) || null,
          createdAt: cleanIsoDateTime(item.createdAt) || new Date().toISOString(),
        };
        items[cleanItem.id] = cleanItem;
      }
    }
    out[id] = { id, name, createdAt, items };
  }
  return out;
}

export function buildBackup(data, householdName) {
  return {
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    householdName: householdName || '',
    data,
  };
}

export function downloadBackup(data, householdName) {
  const payload = buildBackup(data, householdName);
  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safe = String(householdName || 'household').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'household';
  a.href = url;
  a.download = 'budget-backup-' + safe + '-' + todayStr() + '.json';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Parse + validate a backup JSON string.
 * Returns `{ data, warnings }`. `warnings` is an array of human-readable
 * strings describing rows/keys that were dropped or coerced.
 * Throws only when the payload is unrecoverable (not JSON, wrong root type).
 */
export function parseBackupWithMeta(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    throw new Error('Not a valid JSON file.');
  }
  if (!parsed || typeof parsed !== 'object') throw new Error('Backup file is empty or malformed.');

  const inner = parsed.data && typeof parsed.data === 'object' ? parsed.data : parsed;
  if (!Array.isArray(inner.txns) && inner.txns !== undefined) throw new Error('Backup missing a valid txns array.');
  if (inner.cats && typeof inner.cats !== 'object') throw new Error('Backup has an invalid cats object.');

  const warnings = [];

  const txns = [];
  for (let i = 0; i < (Array.isArray(inner.txns) ? inner.txns.length : 0); i++) {
    const v = validateTxn(inner.txns[i], i);
    if (v.ok) txns.push(v.clean);
    else warnings.push(v.reason);
  }

  const bills = [];
  for (let i = 0; i < (Array.isArray(inner.bills) ? inner.bills.length : 0); i++) {
    const v = validateBill(inner.bills[i], i);
    if (v.ok) bills.push(v.clean);
    else warnings.push(v.reason);
  }

  const data = {
    txns,
    bills,
    budgets: validateBudgets(inner.budgets, warnings),
    cats: validateCats(inner.cats, warnings),
    savings: migrateSavings(inner.savings && typeof inner.savings === 'object' ? inner.savings : null),
    netWorth: migrateNetWorth(inner.netWorth && typeof inner.netWorth === 'object' ? inner.netWorth : null),
    settings: validateSettings(inner.settings, warnings),
    categoryMappings: validateCategoryMappings(inner.categoryMappings, warnings),
    shoppingLists: validateShoppingLists(inner.shoppingLists, warnings),
  };

  return { data, warnings };
}

/** Back-compat wrapper — returns the cleaned data object only. */
export function parseBackup(text) {
  return parseBackupWithMeta(text).data;
}

export async function readFileText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read the selected file.'));
    reader.readAsText(file);
  });
}
